import {Injectable} from '@angular/core';
import {BokkzAlgDataService} from './bokkz-alg-data-service';
import {SelectFormControlItem} from '@snelstart/ui-form-controls';
import {ToastService} from '@snelstart/ui-toast';
import {catchError, from, map, Observable, of, take, tap, throwError} from 'rxjs';
import {
  BokkzClient,
  ClientEntity,
  DossierItem,
  IntermediaryEntity,
  PdfRequest,
} from './api.bokkz.services';
import {
  AdministrationQueryClient, AdministrationResult, AdministrationResultWithAvailableModuleActionsOld,
  catchAdministrationNotMigratedError, CustomerClient,
  CustomerQueryClient, CustomerResponse, DistinctValuesResponse,
  IRelationColumnPaginationRequest, KolommenbalansService, RelationColumn,
  RelationColumnPaginationRequest, RelationSearchCriteria,
  RelationSearchResults,
  SortDirection
} from './rekenclient.api';
import {switchMap} from 'rxjs/operators';
import {BokkzAlgemeenClient} from './api.bokkz.algemeen';

@Injectable({providedIn: 'root'})

export class BokkzAlgLogic {
  public clientList = {} as ClientEntity[];
  public client = {} as ClientEntity;
  public intermediary = {} as IntermediaryEntity;
  public taxTypeList = {} as DossierItem[];
  public options: SelectFormControlItem<string>[] = [];

  constructor(
    private bokkzClient: BokkzClient,
    private bokkzAlgemeenClient: BokkzAlgemeenClient,
    private customerQueryClient: CustomerQueryClient,
    private customerClient: CustomerClient,
    private administrationQueryClient: AdministrationQueryClient,
    private kolommenbalans: KolommenbalansService,
    private readonly toastService: ToastService,
    public dataService: BokkzAlgDataService,
  ) {
    //Hier zorgen we ervoor we ervoor dat we nieuwe data binnenkrijgen en stoppen deze in de variabelen.
    this.dataService.client.subscribe((value) => (this.client = value));
    this.dataService.intermediary.subscribe(
      (value) => (this.intermediary = value),
    );
    this.dataService.options.subscribe((value) => (this.options = value));
    this.dataService.dossierList.subscribe(
      (value) => (this.taxTypeList = value),
    );
  }

  private mapAdministration(m: AdministrationResult): AdministrationResultWithAvailableModuleActionsOld {
    const administration = (m as unknown) as AdministrationResultWithAvailableModuleActionsOld;
    if (m.status !== undefined) {
      administration.status = m.status;
    }
    administration.status = m.status;
    administration.type = m.type;
    return administration;
  }

  public postPkiCertificaat(formData: FormData) {
    const pki = formData.get('pki');
    const password = formData.get('password') as string;
    return this.bokkzClient.postPkiCertificate(pki, password).pipe(
      map((response) => {
        this.dataService.updateIntermediary(response);
        this.toastService.showSuccessMessage(
          'Certificaat succesvol opgeslagen.',
        );
        return true;
      }),
      catchError((err) => {
        console.log('pki fout', err);
        this.toastService.showErrorMessage(err);
        return of(false);
      }),
    );
  }

  public postClientFile(formData: FormData) {
    return this.bokkzAlgemeenClient.postClientList(formData).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        console.error('Error:', err);
        return throwError(() => err);
      })
    );
  }

  public getOneIntermediary() {
    this.bokkzClient.getIntermediary().subscribe({
      next: (result) => {
        this.dataService.updateIntermediary(result);
      },
      error: (error) => {
        if (error.status === 500) {
          this.toastService.showErrorMessage(error.message);
        }
        if (error.status === 401) {
          this.toastService.showErrorMessage(error.message);
        }
      },
      // complete: () => console.log("complete call get intermediary")
    });
  }

  public getPdfRapport(pdfRequest: PdfRequest) {
    this.bokkzClient.getPdfRapport(pdfRequest).subscribe({
      next: (result) => {
        this.dataService.updateIbPdfRapport(result);
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

    public postOneIntermediary(intermediary: IntermediaryEntity) {
        this.bokkzClient.postIntermediary(intermediary).subscribe({
            next: (result) => {
                this.dataService.updateIntermediary(result);
            },
            complete: () => {
                this.toastService.showSuccessMessage(
                    'De kantoorinstellingen zijn succesvol opgeslagen.'
                );
            },
            error: (error) => {
                console.log(error);
                this.toastService.showErrorMessage(`De kantoorinstellingen zijn niet opgeslagen (melding: ${error.message}).`);
            }
        });
    }

  public getDossiers(clientId: string) {
    this.bokkzClient.getDossiers(clientId).subscribe({
      next: (result) => {
        this.taxTypeList = result;
        this.dataService.updateTaxTypeList(result);
      },
      error: (error) => {
        console.log(error);
      },
      // complete: () => console.log("complete")
    });
  }

  public getAllAdministrations(): Observable<AdministrationResultWithAvailableModuleActionsOld[]> {
    return this.administrationQueryClient.administrations()
      .pipe(
        map((models: AdministrationResultWithAvailableModuleActionsOld[]) =>
          models
            .map(m => this.mapAdministration(m))));
  }

  public getDistinctValues(columnName: RelationColumn, maxResults?: number): Observable<DistinctValuesResponse> {
    return this.customerQueryClient.getDistinctValues(columnName, maxResults);
  }

  public getPaginated(criteria: RelationSearchCriteria): Observable<RelationSearchResults> {
    const request = new RelationColumnPaginationRequest();
    request.init(criteria);
    return this.customerQueryClient.getPaginated(request)
      .pipe(catchAdministrationNotMigratedError(this.toastService),
        take(1),
        map((response: RelationSearchResults) => {
          const filteredResults = response.results?.filter(r => r.code >= 0) ?? [];
          return {
            ...response,
            results: filteredResults,
            total: response.total - 1
          } as RelationSearchResults;
        })
      );
  }

  public deleteCustomer(id: string): Observable<CustomerResponse> {
    return this.customerClient.deleteCustomer(id).pipe(catchAdministrationNotMigratedError(this.toastService));
  }

  public archiveCustomer(id: string): Observable<CustomerResponse> {
    return this.customerClient.archiveCustomer(id).pipe(catchAdministrationNotMigratedError(this.toastService));
  }

  public deArchiveCustomer(id: string): Observable<CustomerResponse> {
    return this.customerClient.deArchiveCustomer(id).pipe(catchAdministrationNotMigratedError(this.toastService));
  }

  public verrijkToken(administrationId: string): Observable<string> {
    return from(this.kolommenbalans.getAccesstoken(administrationId));
  }

  public openRelatiebeheer(administrationId: string): Observable<RelationSearchResults> {
    const token$ = from(
      this.kolommenbalans.getAccesstoken(administrationId)
    );

    return token$.pipe(
      switchMap(() => {
        const test: IRelationColumnPaginationRequest = {
          page: 0,
          pageSize: 25,
          searchTerm: '',
          sortByColumn: RelationColumn.Code,
          sortDirection: SortDirection.Descending,
        };

        const request = new RelationColumnPaginationRequest();
        request.init(test);

        return this.customerQueryClient
          .getPaginated(request)
          .pipe(
            catchAdministrationNotMigratedError(this.toastService),
            take(1)
          );
      })
    );
  }
}
